import { lazy, Suspense } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import ProtectedRoute from "./ProtectedRoute";
import InstitutionStaffDetailScreen from "@/pages/admin/InstitutionStaffDetailScreen";
import InstitutionStaffs from "@/pages/admin/InstitutionStaffs";
import HubEnrollmentDetailScreen from "@/pages/admin/HubEnrollmentDetailScreen";

// Lazy load layouts
const AppLayout = lazy(() => import("../layouts/AppLayout"));
const AuthLayout = lazy(() => import("../layouts/AuthLayout"));

// Lazy load pages
const Home = lazy(() => import("../pages/Home"));

// Profile page
const ProfilePage = lazy(() => import("../pages/profile/ProfilePage"));

// Admin pages
const RazorpayImportPage = lazy(
  () => import("../pages/admin/RazorpayImportPageRefactored"),
);
const CourseBatchEnrollmentImportPage = lazy(
  () => import("../pages/admin/CourseBatchEnrollmentImportPage"),
);
const AdminCourses = lazy(() => import("../pages/admin/AdminCourses"));
const AdminCourseDetail = lazy(
  () => import("../pages/admin/AdminCourseDetail"),
);
const OnlineCourseSections = lazy(
  () => import("../pages/admin/OnlineCourseSections"),
);
const OnlineCourseLectures = lazy(
  () => import("../pages/admin/OnlineCourseLectures"),
);
const OnlineCourseAnalyticsDashboard = lazy(
  () => import("../pages/admin/OnlineCourseAnalyticsDashboard"),
);
const AdminUsersScreen = lazy(() => import("../pages/admin/AdminUsersScreen"));
const AdminUserEditScreen = lazy(
  () => import("../pages/admin/AdminUserEditScreen"),
);
const AdminCourseBatches = lazy(
  () => import("../pages/admin/AdminCourseBatches"),
);
const AdminCourseBatchDetail = lazy(
  () => import("../pages/admin/AdminCourseBatchDetail"),
);
const AdminCourseEnrollments = lazy(
  () => import("../pages/admin/AdminCourseEnrollments"),
);
const InstitutionLocations = lazy(
  () => import("../pages/admin/InstitutionLocations"),
);
const InstitutionLocationDetailScreen = lazy(
  () => import("../pages/admin/InstitutionLocationDetailScreen"),
);

// Hub features
const HubFeaturesScreen = lazy(
  () => import("../pages/admin/HubFeaturesScreen"),
);
const HubFeatureDetailScreen = lazy(
  () => import("../pages/admin/HubFeatureDetailScreen"),
);

// Hub tier
const HubTiersScreen = lazy(() => import("../pages/admin/HubTiersScreen"));
const HubTierDetailScreen = lazy(
  () => import("../pages/admin/HubTierDetailScreen"),
);

// Hub Plan
const HubPlanDetailScreen = lazy(
  () => import("../pages/admin/HubPlanDetailScreen"),
);

// Hub EnrollmentScreen
const HubEnrollmentsScreen = lazy(
  () => import("../pages/admin/HubEnrollmentsScreen"),
);

// Offers
const AdminOffersPage = lazy(() => import("../pages/admin/AdminOffersPage"));
const AdminOfferDetailPage = lazy(
  () => import("../pages/admin/AdminOfferDetailPage"),
);

// Admin Live Events
const AdminLiveEventsPage = lazy(
  () => import("../pages/admin/AdminLiveEventsPage"),
);
const AdminLiveEventDetailPage = lazy(
  () => import("../pages/admin/AdminLiveEventDetailPage"),
);

const UserInformationFormPage = lazy(
  () => import("../pages/profile/UserInformationFormPage"),
);

// Event pages
const MyLiveEvents = lazy(() => import("../pages/events/MyLiveEvents"));
const LiveEventBookings = lazy(
  () => import("../pages/events/LiveEventBookings"),
);
const LiveEventBookingDetail = lazy(
  () => import("../pages/events/LiveEventBookingDetail"),
);
const LiveEventEnrollPage = lazy(
  () => import("../pages/events/LiveEventEnrollPage"),
);
const UserLiveEventsPage = lazy(
  () => import("../pages/events/UserLiveEventsPage"),
);
const UserLiveEventDetailPage = lazy(
  () => import("../pages/events/UserLiveEventDetailPage"),
);

// Course pages
const CoursesListPage = lazy(() => import("../pages/courses/CoursesListPage"));
const CourseDetailPage = lazy(
  () => import("../pages/courses/CourseDetailPage"),
);
const CourseEnrollPage = lazy(
  () => import("../pages/courses/CourseEnrollPage"),
);
const MyCoursesPage = lazy(() => import("../pages/courses/MyCoursesPage"));
const CourseEnrollmentDetailPage = lazy(
  () => import("../pages/courses/CourseEnrollmentDetailPage"),
);

// payments
const PaymentPlansPage = lazy(
  () => import("../pages/payment/PaymentPlansPage"),
);
const PaymentPlanDetailPage = lazy(
  () => import("../pages/payment/PaymentPlanDetailPage"),
);

// Media pages
const MediaLibraryPage = lazy(() => import("../pages/media/MediaLibraryPage"));

// Hub pages
const HubTierComparisonPage = lazy(
  () => import("../pages/hub/HubTierComparisonPage"),
);
const HubPlanSelectionPage = lazy(
  () => import("../pages/hub/HubPlanSelectionPage"),
);
const HubEnrollmentsPage = lazy(
  () => import("../pages/hub/HubEnrollmentsPage"),
);
const HubEnrollmentDetailPage = lazy(
  () => import("../pages/hub/HubEnrollmentDetailPage"),
);

// Auth pages
const Login = lazy(() => import("../pages/auth/Login"));
const Signup = lazy(() => import("../pages/auth/Signup"));
const ForgotPassword = lazy(() => import("../pages/auth/ForgotPassword"));
const NotFound = lazy(() => import("../pages/NotFound"));

// Loading fallback for lazy-loaded components
const LoadingFallback = () => (
  <div className="flex h-screen w-full items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
  </div>
);

const router = createBrowserRouter(
  [
    {
      path: "/",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <Home />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/events",
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <AppLayout>
            <Suspense fallback={<LoadingFallback />}>
              <UserLiveEventsPage />
            </Suspense>
          </AppLayout>
        </Suspense>
      ),
    },
    {
      path: "/events/my-bookings",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <MyLiveEvents />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/events/:id",
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <AppLayout>
            <Suspense fallback={<LoadingFallback />}>
              <UserLiveEventDetailPage />
            </Suspense>
          </AppLayout>
        </Suspense>
      ),
    },
    {
      path: "/events/:id/enroll",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <LiveEventEnrollPage />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/events/:eventId/bookings",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <LiveEventBookings />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/events/:eventId/bookings/:bookingId",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <LiveEventBookingDetail />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    // Course routes
    {
      path: "/courses",
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <AppLayout>
            <Suspense fallback={<LoadingFallback />}>
              <CoursesListPage />
            </Suspense>
          </AppLayout>
        </Suspense>
      ),
    },
    {
      path: "/courses/my-courses",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <MyCoursesPage />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/courses/enrollments/:id",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <CourseEnrollmentDetailPage />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/courses/:id",
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <AppLayout>
            <Suspense fallback={<LoadingFallback />}>
              <CourseDetailPage />
            </Suspense>
          </AppLayout>
        </Suspense>
      ),
    },
    {
      path: "/courses/:courseId/enroll/:batchId",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <CourseEnrollPage />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    // Hub routes
    {
      path: "/hub/tiers",
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <AppLayout>
            <Suspense fallback={<LoadingFallback />}>
              <HubTierComparisonPage />
            </Suspense>
          </AppLayout>
        </Suspense>
      ),
    },
    {
      path: "/hub/plans/:tierId",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <HubPlanSelectionPage />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/hub/enrollments",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <HubEnrollmentsPage />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/hub/enrollments/:id",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <HubEnrollmentDetailPage />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    // payments
    {
      path: "/admin/payment-plans",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <PaymentPlansPage />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },

    // payment create

    {
      path: "/admin/payment-plans/new",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <PaymentPlanDetailPage />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },

    {
      path: "/admin/payment-plans/:id",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <PaymentPlanDetailPage />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },

    // Media Library route
    {
      path: "/media",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <MediaLibraryPage />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/auth",
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <AuthLayout />
        </Suspense>
      ),
      children: [
        {
          path: "login",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Login />
            </Suspense>
          ),
        },
        {
          path: "signup",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Signup />
            </Suspense>
          ),
        },
        {
          path: "forgot-password",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ForgotPassword />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: "/profile",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <ProfilePage />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/profile/user-information",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <UserInformationFormPage />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/razorpay-import",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <RazorpayImportPage />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/course-batch-enrollment-import",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <CourseBatchEnrollmentImportPage />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/courses",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <AdminCourses />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/courses/:id",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <AdminCourseDetail />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/offers",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <AdminOffersPage />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/offers/:id",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <AdminOfferDetailPage />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/live-events",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <AdminLiveEventsPage />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/live-events/:id",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <AdminLiveEventDetailPage />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/live-events/:eventId/bookings",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <LiveEventBookings />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/live-events/:eventId/bookings/:bookingId",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <LiveEventBookingDetail />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/courses/:courseId/online-course/sections",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <OnlineCourseSections />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/courses/:courseId/online-course/sections/:sectionId/lectures",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <OnlineCourseLectures />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/courses/:courseId/analytics",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <OnlineCourseAnalyticsDashboard />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/users",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <AdminUsersScreen />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/users/:id",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <AdminUserEditScreen />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/users/:id/edit",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <AdminUserEditScreen />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/course-batches",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <AdminCourseBatches />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/course-batches/:id",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <AdminCourseBatchDetail />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/course-batches/:id/edit",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <AdminCourseBatchDetail />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/institutions/locations",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <InstitutionLocations />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/hubs/features",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <HubFeaturesScreen />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/hubs/enrollments",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <HubEnrollmentsScreen />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/hubs/enrollments/detail/:id",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <HubEnrollmentDetailScreen />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/hubs/features/:id",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <HubFeatureDetailScreen />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/hubs/tiers",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <HubTiersScreen />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/hubs/tiers/:id",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <HubTierDetailScreen />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },

    {
      path: "/admin/hubs/plans/:id",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <HubPlanDetailScreen />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/institutions/locations/:id",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <InstitutionLocationDetailScreen />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/institutions/staff",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <InstitutionStaffs />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/institutions/staff/:id",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <InstitutionStaffDetailScreen />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin/course-enrollments",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <AdminCourseEnrollments />
              </Suspense>
            </AppLayout>
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "*",
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <NotFound />
        </Suspense>
      ),
    },
  ],
  { basename: "/" },
);

export default function Routes() {
  return <RouterProvider router={router} />;
}
